import React, {
    Fragment,
    createRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import paginationFactoryImport from 'react-bootstrap-table2-paginator'
import BootstrapTableImport from 'react-bootstrap-table-next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ColumnDescription } from 'react-bootstrap-table-next'
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from 'reactstrap'

import { useAppSelector } from '../../../hooks/redux'
import Avatar from '../../../components/common/Avatar'
import {
    deleteUser,
    toggleDisableUser,
    User,
    Company,
} from './helpers'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BootstrapTable = (BootstrapTableImport as any).default || BootstrapTableImport
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const paginationFactory = (paginationFactoryImport as any).default || paginationFactoryImport

interface RowActionsDropdownProps {
    children: React.ReactNode
}

const RowActionsDropdown = ({ children }: RowActionsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false)
    return (
        <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)}>
            {children}
        </Dropdown>
    )
}

interface AuthedUser {
    uid?: string
    superAdmin?: boolean
}

interface UsersTableProps {
    toggleModal: (showModal?: boolean, user?: User | null) => void
}

/**
 * Table listing all users with edit, delete, and enable/disable actions.
 */
const UsersTable = ({ toggleModal }: UsersTableProps) => {
    const table = createRef()
    const users = useAppSelector(state => state.superAdmin.users) as User[]
    const authedUser = useAppSelector(state => state.auth.user) as AuthedUser | null
    const companies = useAppSelector(state => state.superAdmin.companies) as Company[]
    const { t } = useTranslation()

    const nameFormatter = (_cell: unknown, user: User) => {
        const userName = `${user.name} ${user.surname}`
        const userAvatar = <Avatar size="l" name={userName} className="mr-2" />
        return (
            <div className="d-flex align-items-center">{userAvatar}{userName}</div>
        )
    }

    const companyFormatter = (cell: unknown) => {
        const uid = cell as string
        const companyName = companies?.find?.(company => company.uid === uid)?.name || '#'
        return <>{companyName}</>
    }

    const actionFormatter = (_cell: unknown, row: User) => {
        const user = row
        return (
            <RowActionsDropdown>
                <DropdownToggle color="link" size="sm" className="text-600 btn-reveal mr-3">
                    <FontAwesomeIcon icon="ellipsis-h" className="fs--1" />
                </DropdownToggle>
                <DropdownMenu positionFixed={true} right className="border py-2">
                    <DropdownItem onClick={() => toggleModal(true, user)}>
                        {t('users.editUser')}
                    </DropdownItem>
                    {authedUser?.uid !== user?.uid &&
                        <Fragment>
                            <DropdownItem
                                onClick={() => window.swconfirm({
                                    title: t('users.deleteUserPrompt', { userName: `${user.name} ${user.surname}` }),
                                    onConfirm: () => deleteUser(user),
                                })}>
                                {t('users.deleteUser')}
                            </DropdownItem>
                            <DropdownItem onClick={() => {
                                if (user.approved) {
                                    window.swconfirm({
                                        title: t('users.disableUserPrompt', { userName: `${user.name} ${user.surname}` }),
                                        onConfirm: () => toggleDisableUser(user, !user.approved),
                                    })
                                } else {
                                    toggleDisableUser(user, !user.approved)
                                }
                            }}>
                                {user?.approved ? t('users.disableUser') : t('users.enableUser')}
                            </DropdownItem>
                        </Fragment>
                    }
                </DropdownMenu>
            </RowActionsDropdown>
        )
    }

    const columns: ColumnDescription<User>[] = [
        {
            dataField: 'uid',
            text: 'Jméno',
            formatter: nameFormatter,
            classes: 'border-0 align-middle text-900 ',
            headerClasses: 'border-0',
            sort: true,
        },
        {
            dataField: 'email',
            text: 'Email',
            classes: 'border-0 align-middle text-700',
            headerClasses: 'border-0',
            sort: true,
        },
        {
            dataField: 'company',
            text: 'Společnost',
            formatter: companyFormatter,
            classes: 'border-0 align-middle text-700',
            headerClasses: 'border-0',
            sort: true,
        },
        {
            dataField: 'uid',
            text: '',
            isDummyField: true,
            classes: 'py-2 align-middle',
            formatter: actionFormatter,
            align: 'right',
        },
    ]

    const options = {
        paginationSize: 3,
        pageStartIndex: 1,
        sizePerPage: 10,
        totalSize: users.length,
        hidePageListOnlyOnePage: true,
        prePageText: '<',
        nextPageText: '>',
        hideSizePerPage: true,
        showTotal: false,
    }

    return (
        <BootstrapTable
            ref={table}
            bootstrap4
            keyField='uid'
            data={users}
            columns={columns}
            bordered={false}
            classes='table-dashboard table-striped table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap text-black'
            rowClasses='btn-reveal-trigger border-top border-200'
            headerClasses='bg-200 text-900 border-y border-200'
            pagination={users.length > 10 ? paginationFactory(options) : null}
        />
    )
}

export default UsersTable
